import { io } from 'socket.io-client'

let socket = null
let socketMode = null
let socketOptions = {}
let rawReconnectTimer = null
let rawReconnectAttempts = 0
let rawManualClose = false
let autoFallbackUsed = false

const RAW_MAX_RECONNECT_ATTEMPTS = 5
const RAW_RECONNECT_DELAY = 1000
const SOCKET_EVENT_HANDLERS = {
  success: 'onBindSuccess',
  bind_status: 'onBindStatus',
  new_message: 'onNewMessage',
  new_notice: 'onNewNotice',
  new_leave_application: 'onNewLeaveApplication',
  approval_result: 'onApprovalResult',
  error: 'onError'
}

function normalizePath(path, fallback) {
  const value = String(path || fallback || '').trim()
  if (!value) return ''
  return value.startsWith('/') ? value : `/${value}`
}

function getBaseOrigin() {
  const base = process.env.VUE_APP_SOCKET_ORIGIN || process.env.VUE_APP_PROXY_TARGET || process.env.VUE_APP_API_BASE || ''
  if (base) {
    try {
      return new URL(base).origin
    } catch (_) {
      void _
    }
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return ''
}

function toWebSocketOrigin(origin) {
  if (!origin) return ''
  if (origin.startsWith('https://')) return `wss://${origin.slice(8)}`
  if (origin.startsWith('http://')) return `ws://${origin.slice(7)}`
  return origin
}

function buildRawSocketUrl(path, token) {
  const baseOrigin = getBaseOrigin()
  const url = `${toWebSocketOrigin(baseOrigin)}${normalizePath(path, '/ws/device')}`
  if (!token) return url
  try {
    const parsed = new URL(url)
    parsed.searchParams.set('token', token)
    return parsed.toString()
  } catch (_) {
    void _
    return url
  }
}

function getSocketConfig() {
  const mode = String(process.env.VUE_APP_SOCKET_MODE || 'auto').trim().toLowerCase()
  const configuredPath = normalizePath(process.env.VUE_APP_SOCKET_PATH, '')
  const socketIoPath = normalizePath(process.env.VUE_APP_SOCKET_IO_PATH || configuredPath, '/socket.io')
  const rawPath = normalizePath(process.env.VUE_APP_WEBSOCKET_PATH || configuredPath, '/ws/device')

  if (mode === 'socket.io') {
    return { mode: 'socket.io', path: socketIoPath, fallbackPath: '' }
  }

  if (mode === 'websocket') {
    return { mode: 'websocket', path: rawPath, fallbackPath: '' }
  }

  if (configuredPath) {
    const inferredMode = configuredPath.startsWith('/ws') ? 'websocket' : 'socket.io'
    return {
      mode: inferredMode,
      path: configuredPath,
      fallbackPath: inferredMode === 'socket.io' ? rawPath : ''
    }
  }

  return {
    mode: 'socket.io',
    path: socketIoPath,
    fallbackPath: rawPath
  }
}

function clearRawReconnectTimer() {
  if (rawReconnectTimer) {
    clearTimeout(rawReconnectTimer)
    rawReconnectTimer = null
  }
}

function resetRawReconnectState() {
  clearRawReconnectTimer()
  rawReconnectAttempts = 0
}

function isSocketActive() {
  if (!socket) return false
  if (socketMode === 'socket.io') {
    return Boolean(socket.connected || socket.active)
  }
  return socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING
}

function dispatchSocketEvent(eventName, payload) {
  if (!eventName) return false
  const handlerName = SOCKET_EVENT_HANDLERS[eventName]
  if (!handlerName) return false
  socketOptions[handlerName]?.(payload)
  return true
}

function extractSocketEvent(message) {
  if (!message || typeof message !== 'object') {
    return { eventName: '', payload: message }
  }

  const eventName = message.event || message.type || message.name || message.action || ''
  if (eventName) {
    return {
      eventName,
      payload: message.data ?? message.payload ?? message.body ?? message.message ?? message
    }
  }

  const knownEvent = Object.keys(SOCKET_EVENT_HANDLERS).find(key => key in message)
  if (knownEvent) {
    return { eventName: knownEvent, payload: message[knownEvent] }
  }

  return { eventName: '', payload: message }
}

function safeParseMessage(raw) {
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch (_) {
    return raw
  }
}

function emitRawEvent(event, data = {}) {
  if (!socket || socketMode !== 'websocket' || socket.readyState !== WebSocket.OPEN) return
  const payload = { event }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    Object.assign(payload, data)
    payload.data = data
  } else if (data !== undefined) {
    payload.data = data
  }
  socket.send(JSON.stringify(payload))
}

function bindAfterConnect(token) {
  if (!token) return
  if (socketMode === 'socket.io') {
    socket.emit('user_bind', { token })
    return
  }
  emitRawEvent('user_bind', { token })
}

function enhanceRawSocket(ws) {
  ws.connected = false
  ws.emit = (event, data) => emitRawEvent(event, data)
  ws.disconnect = () => {
    rawManualClose = true
    resetRawReconnectState()
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close(1000, 'client disconnect')
    }
  }
  return ws
}

function scheduleRawReconnect(config) {
  if (rawManualClose || rawReconnectAttempts >= RAW_MAX_RECONNECT_ATTEMPTS) return
  clearRawReconnectTimer()
  rawReconnectAttempts += 1
  rawReconnectTimer = setTimeout(() => {
    openSocket(config, socketOptions)
  }, RAW_RECONNECT_DELAY)
}

function initRawWebSocket(config, options) {
  rawManualClose = false
  const token = options.token ?? localStorage.getItem('token')
  const ws = enhanceRawSocket(new WebSocket(buildRawSocketUrl(config.path, token)))
  socket = ws
  socketMode = 'websocket'

  ws.addEventListener('open', () => {
    ws.connected = true
    resetRawReconnectState()
    options.onConnect?.()
    bindAfterConnect(token)
  })

  ws.addEventListener('message', (event) => {
    const parsed = safeParseMessage(event.data)
    const { eventName, payload } = extractSocketEvent(parsed)
    if (!dispatchSocketEvent(eventName, payload)) {
      options.onMessage?.(payload)
    }
  })

  ws.addEventListener('error', (event) => {
    options.onError?.(event)
  })

  ws.addEventListener('close', (event) => {
    ws.connected = false
    options.onDisconnect?.(event)
    scheduleRawReconnect(config)
  })

  return ws
}

function initSocketIo(config, options) {
  const baseOrigin = (process.env.VUE_APP_SOCKET_ORIGIN || process.env.VUE_APP_PROXY_TARGET)
    ? getBaseOrigin()
    : undefined

  const sio = io(baseOrigin, {
    path: config.path,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    timeout: 20000
  })

  socket = sio
  socketMode = 'socket.io'

  sio.on('connect', () => {
    options.onConnect?.()
    const token = options.token ?? localStorage.getItem('token')
    bindAfterConnect(token)
  })

  sio.on('connect_error', (error) => {
    if (config.fallbackPath && !autoFallbackUsed) {
      autoFallbackUsed = true
      sio.close()
      socket = null
      socketMode = null
      openSocket({ mode: 'websocket', path: config.fallbackPath, fallbackPath: '' }, options)
      return
    }
    options.onError?.(error)
  })

  Object.keys(SOCKET_EVENT_HANDLERS).forEach(eventName => {
    sio.on(eventName, payload => {
      dispatchSocketEvent(eventName, payload)
    })
  })

  sio.on('disconnect', reason => {
    options.onDisconnect?.(reason)
  })

  sio.on('reconnect', () => {
    const token = options.token ?? localStorage.getItem('token')
    bindAfterConnect(token)
  })

  return sio
}

function openSocket(config, options) {
  if (config.mode === 'websocket') {
    return initRawWebSocket(config, options)
  }
  return initSocketIo(config, options)
}

export function initSocket(options = {}) {
  if (isSocketActive()) return socket
  socketOptions = options
  autoFallbackUsed = false
  return openSocket(getSocketConfig(), options)
}

export function bindUser(token) {
  if (!token || !socket) return
  bindAfterConnect(token)
}

export function disconnectSocket() {
  resetRawReconnectState()
  rawManualClose = true

  if (!socket) return

  if (socketMode === 'socket.io') {
    socket.disconnect()
  } else if (typeof socket.disconnect === 'function') {
    socket.disconnect()
  }

  socket = null
  socketMode = null
}

export function getSocket() {
  return socket
}
